/**
 * Audio Playback Utility
 * 
 * Handles connecting MediaStream to audio elements and managing playback
 */

export interface AudioPlaybackConfig {
    autoplay?: boolean;
    volume?: number;
}

export class AudioPlayback {
    private audioElement: HTMLAudioElement | null = null;
    private currentStream: MediaStream | null = null;
    private volume: number = 0.8;
    private autoplay: boolean = true;

    constructor(config?: AudioPlaybackConfig) {
        this.volume = config?.volume ?? 0.8;
        this.autoplay = config?.autoplay ?? true;
    }

    /**
     * Connect MediaStream to audio element and start playback
     */
    async connectStream(stream: MediaStream, audioElement: HTMLAudioElement): Promise<void> {
        try {
            this.audioElement = audioElement;
            this.currentStream = stream;

            // Set the stream as the source
            audioElement.srcObject = stream;
            audioElement.volume = this.volume;

            // Handle autoplay
            if (this.autoplay) {
                await this.startPlayback();
            }
        } catch (error) {
            throw new Error(`Failed to connect audio stream: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Start audio playback
     * Handles autoplay policies by catching and rethrowing errors
     */
    async startPlayback(): Promise<void> {
        if (!this.audioElement) {
            throw new Error('No audio element connected');
        }

        try {
            await this.audioElement.play();
        } catch (error) {
            // Handle autoplay policy errors
            if (error instanceof Error) {
                if (error.name === 'NotAllowedError') {
                    throw new Error('Autoplay blocked. Please click to start playback.');
                } else if (error.name === 'NotSupportedError') {
                    throw new Error('Audio playback not supported on this device.');
                }
            }
            throw new Error(`Failed to start playback: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Stop audio playback
     */
    stopPlayback(): void {
        if (this.audioElement) {
            this.audioElement.pause();
            this.audioElement.srcObject = null;
        }
        this.currentStream = null;
    }

    /**
     * Set volume (0.0 to 1.0)
     */
    setVolume(volume: number): void {
        if (volume < 0 || volume > 1) {
            throw new Error('Volume must be between 0 and 1');
        }

        this.volume = volume;
        if (this.audioElement) {
            this.audioElement.volume = volume;
        }
    }

    /**
     * Get current volume
     */
    getVolume(): number {
        return this.volume;
    }

    /**
     * Check if audio is currently playing
     */
    isPlaying(): boolean {
        if (!this.audioElement) {
            return false;
        }
        return !this.audioElement.paused && !this.audioElement.ended;
    }

    /**
     * Get current stream
     */
    getStream(): MediaStream | null {
        return this.currentStream;
    }

    /**
     * Clean up resources
     */
    cleanup(): void {
        this.stopPlayback();
        this.audioElement = null;
        this.currentStream = null;
    }
}
